import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Apple, Target, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface NutritionalAssessmentCardProps {
  records: any[];
  onRecordsChange: (updatedRecords: any[]) => void;
}

export function NutritionalAssessmentCard({ records, onRecordsChange }: NutritionalAssessmentCardProps) {
  const nutritionRecords = records.filter(r => r.type === 'nutritional_data' || r.type === 'meal');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  if (nutritionRecords.length === 0) return null;

  const handleEdit = (idx: number) => {
    const originalIndex = records.indexOf(nutritionRecords[idx]);
    const record = records[originalIndex];
    setEditForm({
      ...record,
      foods: Array.isArray(record.foods) ? record.foods.join(', ') : record.foods || ''
    });
    setEditingIndex(originalIndex);
  };

  const handleSaveEdit = () => {
    if (editingIndex === null) return;
    const updated = [...records];
    const formToSave = {
      ...editForm,
      foods: editForm.type === 'meal' && editForm.foods
        ? editForm.foods.split(',').map((f: string) => f.trim()).filter(Boolean)
        : editForm.foods
    };
    updated[editingIndex] = formToSave;
    onRecordsChange(updated);
    setEditingIndex(null);
    toast.success("Registro atualizado!");
  };

  const handleDelete = (idx: number) => {
    const originalIndex = records.indexOf(nutritionRecords[idx]);
    setDeleteIndex(originalIndex);
  };

  const confirmDelete = () => {
    if (deleteIndex === null) return;
    const updated = records.filter((_, i) => i !== deleteIndex);
    onRecordsChange(updated);
    setDeleteIndex(null);
    toast.success("Registro excluído!");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Apple className="h-5 w-5 text-green-600" />
        <h3 className="text-lg font-semibold text-green-600">
          {nutritionRecords.some(r => r.type === 'meal') ? 'Plano Alimentar' : 'Avaliação Nutricional'}
        </h3>
        <Badge variant="secondary">{nutritionRecords.length} registros</Badge>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {nutritionRecords.map((record, idx) => (
          <Card key={idx} className="border-l-4 border-l-green-600">
            <CardContent className="pt-4">
              <div className="flex justify-end gap-2 mb-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEdit(idx)}
                  className="text-primary hover:bg-primary/10"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(idx)}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {record.type === 'meal' ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-foreground capitalize">
                      {record.meal_name}
                    </span>
                    {record.time && (
                      <span className="text-xs text-muted-foreground">{record.time}</span>
                    )}
                  </div>

                  {record.foods && (
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      {(Array.isArray(record.foods) ? record.foods : [record.foods]).map((food: string, i: number) => (
                        <li key={i}>{food}</li>
                      ))}
                    </ul>
                  )}

                  {record.calories && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-secondary">{record.calories} kcal</span>
                      {record.protein && <span className="text-muted-foreground">P: {record.protein}g</span>}
                      {record.carbs && <span className="text-muted-foreground">C: {record.carbs}g</span>}
                      {record.fats && <span className="text-muted-foreground">G: {record.fats}g</span>}
                    </div>
                  )}

                  {record.notes && (
                    <p className="text-xs text-muted-foreground italic">{record.notes}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-muted-foreground capitalize">
                      {record.measurement_type?.replace(/_/g, ' ')}
                    </span>
                    {record.date && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(record.date).toLocaleDateString('pt-BR')}
                      </span>
                    )}
                  </div>

                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-secondary">
                      {record.value}
                    </span>
                    {record.unit && (
                      <span className="text-sm text-muted-foreground">{record.unit}</span>
                    )}
                  </div>

                  {record.diagnosis && (
                    <div className="flex items-center gap-1 text-sm">
                      <Target className="h-3 w-3 text-orange-500" />
                      <span className="text-muted-foreground">{record.diagnosis}</span>
                    </div>
                  )}

                  {record.goal && (
                    <div className="bg-green-50 border border-green-200 rounded p-2 text-xs">
                      🎯 <span className="font-medium">Meta:</span> {record.goal}
                    </div>
                  )}

                  {record.conduct && (
                    <p className="text-xs text-muted-foreground">
                      📋 {record.conduct}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <AlertDialog open={editingIndex !== null} onOpenChange={() => setEditingIndex(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {editForm.type === 'meal' ? 'Editar Refeição' : 'Editar Avaliação Nutricional'}
            </AlertDialogTitle>
          </AlertDialogHeader>

          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            {editForm.type === 'meal' ? (
              <>
                <div className="grid gap-2">
                  <Label>Nome da Refeição</Label>
                  <Input
                    value={editForm.meal_name || ""}
                    onChange={(e) => setEditForm({ ...editForm, meal_name: e.target.value })}
                    placeholder="Ex: Café da manhã"
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Horário</Label>
                  <Input
                    type="time"
                    value={editForm.time || ""}
                    onChange={(e) => setEditForm({ ...editForm, time: e.target.value })}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Alimentos (separados por vírgula)</Label>
                  <Textarea
                    value={editForm.foods || ""}
                    onChange={(e) => setEditForm({ ...editForm, foods: e.target.value })}
                    placeholder="Ex: Pão integral, Ovo, Café"
                  />
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Calorias</Label>
                    <Input
                      type="number"
                      value={editForm.calories || ""}
                      onChange={(e) => setEditForm({ ...editForm, calories: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Proteínas (g)</Label>
                    <Input
                      type="number"
                      value={editForm.protein || ""}
                      onChange={(e) => setEditForm({ ...editForm, protein: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Carboidratos (g)</Label>
                    <Input
                      type="number"
                      value={editForm.carbs || ""}
                      onChange={(e) => setEditForm({ ...editForm, carbs: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Gorduras (g)</Label>
                    <Input
                      type="number"
                      value={editForm.fats || ""}
                      onChange={(e) => setEditForm({ ...editForm, fats: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={editForm.notes || ""}
                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="grid gap-2">
                  <Label>Tipo de Medição</Label>
                  <Input
                    value={editForm.measurement_type || ""}
                    onChange={(e) => setEditForm({ ...editForm, measurement_type: e.target.value })}
                    placeholder="Ex: peso, altura, IMC"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editForm.value || ""}
                      onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Unidade</Label>
                    <Input
                      value={editForm.unit || ""}
                      onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })}
                      placeholder="Ex: kg, cm, kg/m²"
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Data</Label>
                  <Input
                    type="date"
                    value={editForm.date || ""}
                    onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Diagnóstico</Label>
                  <Input
                    value={editForm.diagnosis || ""}
                    onChange={(e) => setEditForm({ ...editForm, diagnosis: e.target.value })}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Meta</Label>
                  <Input
                    value={editForm.goal || ""}
                    onChange={(e) => setEditForm({ ...editForm, goal: e.target.value })}
                  />
                </div>

                <div className="grid gap-2">
                  <Label>Conduta</Label>
                  <Textarea
                    value={editForm.conduct || ""}
                    onChange={(e) => setEditForm({ ...editForm, conduct: e.target.value })}
                  />
                </div>
              </>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveEdit}>Salvar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteIndex !== null} onOpenChange={() => setDeleteIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}